import { LoadingFieldSkeleton } from 'components/secondaryMarket/shared';
import { FeedbackMessage } from 'components/secondaryMarket/shared/FeedbackMessage';
import type { TagItem } from 'components/shared/Tags';
import { SgTagsPicker } from 'components/shared/Tags';
import { FC, useCallback, useMemo } from 'react';
import type { SalesDesk } from 'state/secondaryMarket/cpOrder/referential/client/client.model';
import type { LoadingStatus } from 'utils/status';

export type SalesDeskSelectorProps = {
  salesDeskLoadingStatus: LoadingStatus;
  displayErrors?: boolean;
  selectedSalesDesk?: string;
  availableDesks: readonly SalesDesk[];
  isFrozen: boolean;
  onSalesDeskChanged: (salesId: string | undefined) => void;
};

export const SalesDeskSelector: FC<SalesDeskSelectorProps> = ({
  availableDesks,
  salesDeskLoadingStatus,
  isFrozen,
  selectedSalesDesk,
  onSalesDeskChanged,
  displayErrors = true,
}) => {
  const availableItems = useMemo(
    () => availableDesks?.map(t => ({ key: t.desk, label: t.desk }) as TagItem),
    [availableDesks],
  );
  const singleSelection = useMemo(
    () => (selectedSalesDesk !== undefined ? [selectedSalesDesk] : []),
    [selectedSalesDesk],
  );
  const onSingleSelect = useCallback(
    (selection: readonly string[]) => {
      if (selection?.length === 1) {
        onSalesDeskChanged(selection[0]);
      }
    },
    [onSalesDeskChanged],
  );

  if (salesDeskLoadingStatus === 'loading') {
    return <LoadingFieldSkeleton />;
  }
  if (salesDeskLoadingStatus === 'idle') {
    return null;
  }
  return (
    <>
      <SgTagsPicker
        id="SalesDesk"
        onSelectionKeysChanged={onSingleSelect}
        singleSelect
        noIcon
        selection={singleSelection}
        available={availableItems}
        disabled={isFrozen}
        maxDisplayedItems={availableItems?.length}
        noClear
        hasTooltip
        placeholder="Sales desk"
        e2e="sales-desk"
      />
      {salesDeskLoadingStatus === 'error' && displayErrors && (
        <FeedbackMessage labelKey="secondaryMarket.errors.salesCoverageLoadFailed" color="danger" />
      )}
    </>
  );
};

SalesDeskSelector.displayName = 'SalesDeskSelector';
